import { recordEventAndFlush } from "@/services/telemetry";

const STREAK_KEY = "boot.reloadStreak";

/**
 * Consecutive page loads that never reach interactive before the session counts as a reload loop. A
 * single reload is normal; several loads in a row that never become interactive is a stuck user.
 */
export const RELOAD_LOOP_THRESHOLD = 3;

/**
 * `sessionStorage`, not `localStorage`, so the streak has same-tab reload semantics: it survives
 * `location.reload()` — including the auto-recovery reload, which wipes IndexedDB but not session
 * storage — yet never leaks across tabs (three tabs of one slow workspace must not read as a loop).
 */
function readStreak(): number {
  try {
    return Number(sessionStorage.getItem(STREAK_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Increments and returns the count of this tab's page loads that have not yet reached interactive.
 * {@link resetBootReloadStreak} clears it once a boot reaches interactive.
 */
export function incrementBootReloadStreak(): number {
  const streak = readStreak() + 1;
  try {
    sessionStorage.setItem(STREAK_KEY, String(streak));
  } catch {
    // sessionStorage unavailable — the returned count still drives this load's report.
  }
  return streak;
}

/** Clears the streak; call once a boot reaches interactive so the next load starts fresh. */
export function resetBootReloadStreak(): void {
  try {
    sessionStorage.removeItem(STREAK_KEY);
  } catch {
    // sessionStorage unavailable — nothing actionable.
  }
}

/**
 * Emits one flushed `boot.reload_loop` event carrying the cycle count once the streak reaches the
 * loop threshold, so the stuck-user alert can fire on the count rather than raw event volume — a
 * looping page reloads before most buffered events ship, so volume badly under-reports a real
 * outage. No-op below the threshold.
 */
export function reportBootReloadLoop(streak: number): void {
  if (streak < RELOAD_LOOP_THRESHOLD) return;
  void recordEventAndFlush("boot.reload_loop", { boot: { reloadStreak: streak } });
}
